// Pure helpers for the User Roles redesign. No API calls, no JSX.
// The permission matrix data (groups/flags) comes from `/permission-matrix/modules`.

export interface MatrixActionIds {
  view: number | null;
  create: number | null;
  edit: number | null;
  delete: number | null;
}
export interface MatrixModule {
  module_key: string;
  prefix: string;
  display_name: string;
  category: string;
  actions: MatrixActionIds;
}
export interface MatrixGroup {
  category: string;
  modules: MatrixModule[];
}
export interface MatrixFlag {
  id: number;
  name: string;
  label: string;
  category: string;
}

export const ACTION_KEYS = ["view", "create", "edit", "delete"] as const;
export type ActionKey = (typeof ACTION_KEYS)[number];

export const ACTIONS: { key: ActionKey; label: string; color: string }[] = [
  { key: "view", label: "View", color: "#2563eb" },
  { key: "create", label: "Create", color: "#16a34a" },
  { key: "edit", label: "Edit", color: "#d97706" },
  { key: "delete", label: "Delete", color: "#dc2626" },
];

// "Student_View" -> "Student", "Program_Library_Create" -> "Program Library"
export const prettify = (raw: string): string =>
  raw
    .replace(/_(View|Create|Edit|Delete)$/i, "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Unique module display names present in a role's permission-name list.
export const groupNamesToModules = (allPrivileges: string[] = []): string[] => {
  const set = new Set<string>();
  allPrivileges.forEach((name) => {
    const label = prettify(name);
    if (label) set.add(label);
  });
  return Array.from(set);
};

// Short human summary of what a role can touch, derived from its modules.
export const deriveDescription = (allPrivileges: string[] = []): string => {
  const modules = groupNamesToModules(allPrivileges);
  if (modules.length === 0) return "No access configured";
  const shown = modules.slice(0, 3).join(", ");
  const extra = modules.length - 3;
  return extra > 0 ? `Access to ${shown} +${extra} more` : `Access to ${shown}`;
};

// ---- Privilege catalog helpers (legacy `privileges` table) -----------------

export const CRUD = ["View", "Create", "Edit", "Delete"] as const;
export type Crud = (typeof CRUD)[number];

export const CRUD_ACTIONS: { key: Crud; color: string }[] = [
  { key: "View", color: "#2563eb" },
  { key: "Create", color: "#16a34a" },
  { key: "Edit", color: "#d97706" },
  { key: "Delete", color: "#dc2626" },
];

export interface ParsedPrivilege {
  module: string; // raw prefix token, e.g. "ClassSchedule"
  action: Crud | null; // null when the name is not a CRUD permission
}

// "ClassSchedule_View" -> { module: "ClassSchedule", action: "View" }
export const parsePrivilegeName = (name: string): ParsedPrivilege => {
  const m = name.match(/^(.*)_(View|Create|Edit|Delete)$/);
  if (m) return { module: m[1], action: m[2] as Crud };
  return { module: name, action: null };
};

// "ClassSchedule" / "Program_Library" -> "Class Schedule" / "Program Library"
export const humanizeModule = (token: string): string =>
  token
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

// "class schedule" -> "ClassSchedule" (the name token stored before the "_")
export const toModuleToken = (input: string): string =>
  input
    .trim()
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");

// Display-only category bucket for the privilege matrix. Modules are still
// parsed dynamically from the data; this only chooses a section + icon and
// falls back to "Other". Adjust keywords as the catalog grows.
export const deriveCategory = (token: string): string => {
  const c = humanizeModule(token).toLowerCase();
  if (/dashboard/.test(c)) return "Dashboard";
  if (/student|parent|enrol/.test(c)) return "Student Management";
  if (/class|course|program|grade|subject|session|library|workflow|prompt|attendance|assignment/.test(c))
    return "Academic";
  if (/staff|employee|payroll|schedule|position|punch|leave|availab/.test(c))
    return "Staff / HR";
  if (/invoice|payment|receipt|finance|expense|transaction|billing/.test(c))
    return "Finance";
  if (/message|sms|notif|communicat|email/.test(c)) return "Communication";
  if (/report|log|activity/.test(c)) return "Reports";
  if (/user|role|privilege|permission|company|branch|setting|theme|location/.test(c))
    return "Admin";
  return "Other";
};

export type TemplateKey =
  | "admin"
  | "manager"
  | "frontdesk"
  | "finance"
  | "custom";

export const TEMPLATE_OPTIONS: { key: TemplateKey; label: string }[] = [
  { key: "admin", label: "Admin Access" },
  { key: "manager", label: "Manager Access" },
  { key: "frontdesk", label: "Front Desk Access" },
  { key: "finance", label: "Finance Access" },
  { key: "custom", label: "Custom" },
];

const collectModuleIds = (
  groups: MatrixGroup[],
  pred: (m: MatrixModule) => boolean,
  actions: readonly ActionKey[] = ACTION_KEYS
): number[] => {
  const ids: number[] = [];
  groups.forEach((g) =>
    g.modules.forEach((m) => {
      if (!pred(m)) return;
      actions.forEach((a) => {
        const id = m.actions[a];
        if (id) ids.push(id);
      });
    })
  );
  return ids;
};

// Heuristic presets (frontend only). Adjust category mappings here as the
// permission catalog evolves — categories come from the backend permissionGroup.
export const getTemplatePermissionIds = (
  key: TemplateKey,
  groups: MatrixGroup[],
  flags: MatrixFlag[]
): number[] => {
  switch (key) {
    case "admin":
      return [...collectModuleIds(groups, () => true), ...flags.map((f) => f.id)];
    case "manager":
      return [
        ...collectModuleIds(groups, (m) => m.category !== "Admin"),
        ...flags.filter((f) => f.category !== "Admin").map((f) => f.id),
      ];
    case "frontdesk": {
      const cats = ["Dashboard", "Student Management", "Communication"];
      return collectModuleIds(groups, (m) => cats.includes(m.category));
    }
    case "finance":
      return [
        ...collectModuleIds(groups, (m) => m.category === "Dashboard", ["view"]),
        ...collectModuleIds(groups, (m) => m.category === "Finance"),
        ...collectModuleIds(groups, (m) => m.category === "Reports", ["view"]),
      ];
    case "custom":
    default:
      return [];
  }
};
